import type { GigAdminRow, GigDashboard, GigStage } from "../../api/admin";
import { ADMIN_PAGE_SIZE } from "../../api/admin";
import type { Track } from "../../api/types";
import {
  Avatar,
  Badge,
  FilterSelect,
  KpiCard,
  Pager,
  SearchBox,
  StepDots,
} from "../../components/admin/AdminUi";
import { DataTable, type Column } from "../../components/admin/DataTable";
import { Link } from "react-router";

const TRACKS: [Track, string][] = [
  ["uiux", "UI/UX"],
  ["graphic", "Graphic"],
  ["video", "Video"],
  ["digital", "Digital marketing"],
];
const STAGES: [GigStage, string][] = [
  ["notStarted", "Not started"],
  ["inProgress", "In progress"],
  ["workEnabled", "Work-Enabled"],
  ["complete", "Pathway complete"],
];
const TRACK_SHORT: Record<string, string> = {
  uiux: "UI/UX",
  graphic: "Graphic",
  video: "Video",
  digital: "Digital",
};
const KYC: Record<string, [string, "positive" | "warning" | "negative"]> = {
  verified: ["Verified", "positive"],
  pending: ["Pending", "warning"],
  notstarted: ["Not started", "negative"],
};

export interface GigFilterState {
  search: string;
  track: Track | "";
  stage: GigStage | "";
  stalledOnly: boolean;
}

interface GigDashboardScreenProps {
  data: GigDashboard | null;
  loading: boolean;
  filter: GigFilterState;
  page: number;
  selectedId: string | null;
  onFilter: (patch: Partial<GigFilterState>) => void;
  onPage: (page: number) => void;
  onOpen: (row: GigAdminRow) => void;
}

const columns: Column<GigAdminRow>[] = [
  {
    key: "name",
    header: "Participant",
    sortValue: (r) => `${r.firstName} ${r.lastName}`.toLowerCase(),
    render: (r) => (
      <div className="flex items-center gap-3">
        <Avatar first={r.firstName} last={r.lastName} />
        <div className="min-w-0">
          <div className="truncate font-semibold">
            {`${r.firstName} ${r.lastName}`.trim() || "–"}
          </div>
          <div className="truncate text-[12px] text-text-muted">{r.email}</div>
        </div>
      </div>
    ),
  },
  {
    key: "id",
    header: "ID",
    hideBelow: "lg",
    sortValue: (r) => r.participantId,
    render: (r) => (
      <span className="whitespace-nowrap tabular text-text-muted">{r.participantId}</span>
    ),
  },
  {
    key: "track",
    header: "Track",
    hideBelow: "md",
    sortValue: (r) => r.track,
    render: (r) =>
      r.track ? (
        <Badge tone="accent">{TRACK_SHORT[r.track]}</Badge>
      ) : (
        <span className="text-text-muted">–</span>
      ),
  },
  {
    key: "progress",
    header: "Progress",
    sortValue: (r) => r.stepsDone.length,
    render: (r) => (
      <div className="flex flex-col gap-1">
        <StepDots done={r.stepsDone} total={5} label="Gig pathway" />
        {r.stalledDays != null ? (
          <span className="text-[11.5px] font-semibold text-[#8a5300]">
            Stalled {r.stalledDays}d
          </span>
        ) : null}
      </div>
    ),
  },
  {
    key: "bids",
    header: "Bids",
    align: "right",
    hideBelow: "md",
    sortValue: (r) => r.bidsCounted,
    render: (r) => (
      <span className="tabular">
        {r.bidsCounted}
        <span className="text-text-muted">/10</span>
        {r.bidReadiness != null ? (
          <span className="block text-[11.5px] text-text-muted">{r.bidReadiness}% avg</span>
        ) : null}
      </span>
    ),
  },
  {
    key: "kyc",
    header: "KYC",
    hideBelow: "sm",
    sortValue: (r) => r.kycStatus,
    render: (r) =>
      r.kycStatus ? (
        <Badge tone={KYC[r.kycStatus]?.[1]}>{KYC[r.kycStatus]?.[0]}</Badge>
      ) : (
        <span className="text-text-muted">–</span>
      ),
  },
  {
    key: "platforms",
    header: "Platforms",
    hideBelow: "lg",
    sortValue: (r) => r.platformsRegistered.length,
    render: (r) => (
      <span className="tabular" title={r.platformsRegistered.join(", ") || "None registered"}>
        <b>{r.platformsRegistered.length}</b>
        {r.platformsRegistered.length ? (
          <span className="block max-w-[180px] truncate text-[11.5px] text-text-muted">
            {r.platformsRegistered.join(", ")}
          </span>
        ) : null}
      </span>
    ),
  },
  {
    key: "applications",
    header: "Applied",
    align: "right",
    hideBelow: "md",
    sortValue: (r) => r.applications,
    render: (r) =>
      r.applications ? (
        <span className="tabular">{r.applications}</span>
      ) : (
        <span className="text-text-muted">–</span>
      ),
  },
  {
    key: "paidWins",
    header: "Paid wins",
    align: "right",
    hideBelow: "sm",
    sortValue: (r) => r.paidWins,
    render: (r) =>
      r.paidWins ? (
        <span className="font-bold tabular text-[#3c6a2a]">{r.paidWins}</span>
      ) : (
        <span className="text-text-muted">–</span>
      ),
  },
  {
    key: "yiw",
    header: "Youth-in-Work",
    hideBelow: "lg",
    sortValue: (r) => (r.youthInWork ? 1 : 0),
    render: (r) =>
      r.youthInWork ? (
        <Badge tone="positive">In work</Badge>
      ) : (
        <span className="text-text-muted">–</span>
      ),
  },
  {
    key: "files",
    header: "Files",
    hideBelow: "md",
    render: (r) => (
      <span className="flex gap-1.5 text-[12px]">
        <span
          className={r.hasCv ? "font-semibold text-text" : "text-[#c9b6a2]"}
          title={r.hasCv ? "CV on file" : "No CV"}
        >
          CV
        </span>
        <span
          className={r.hasKycScreenshot ? "font-semibold text-text" : "text-[#c9b6a2]"}
          title={r.hasKycScreenshot ? "KYC screenshot on file" : "No KYC screenshot"}
        >
          KYC
        </span>
        <span
          className={r.hasCertificate ? "font-semibold text-text" : "text-[#c9b6a2]"}
          title={r.hasCertificate ? "Certificate on file" : "No certificate"}
        >
          Cert
        </span>
      </span>
    ),
  },
];

/** Gig readiness: who is where, who is stuck, and each person's record one click away. */
export function GigDashboardScreen(props: GigDashboardScreenProps) {
  const { data, loading, filter, page, selectedId, onFilter, onPage, onOpen } = props;
  const filtered = Boolean(filter.search || filter.track || filter.stage || filter.stalledOnly);
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <KpiCard
          label="Participants"
          value={data?.participants ?? "–"}
          tone="accent"
          hint={
            data
              ? data.rosterSize
                ? `${data.rosterSize} on the roster`
                : "No roster: anyone can sign in"
              : undefined
          }
        />
        <KpiCard
          label="Work-Enabled"
          value={data?.workEnabled ?? "–"}
          tone="positive"
          hint={
            data && data.participants
              ? `${Math.round((data.workEnabled / data.participants) * 100)}% of participants`
              : undefined
          }
        />
        <KpiCard
          label="Pathway complete"
          value={data?.pathwayComplete ?? "–"}
          tone="info"
          hint="All five steps done"
        />
        <KpiCard
          label="Stalled 7+ days"
          value={data?.stalled ?? "–"}
          tone="warning"
          hint="No step cleared in a week"
        />
        <KpiCard
          label="Youth-in-Work"
          value={data?.youthInWork ?? "–"}
          tone="positive"
          hint={
            <>
              A paid win reported ·{" "}
              <Link to="/admin/wins" className="font-semibold text-accent hover:underline">
                See all wins
              </Link>
            </>
          }
        />
        <KpiCard
          label="Gig Board applications"
          value={data?.applications ?? "–"}
          tone="info"
          hint="Clicks on Apply, all participants"
        />
      </div>

      {data ? (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <ChipGroup
            title="Steps completed"
            chips={data.byStepsDone.map((count, i) => ({
              key: String(i),
              label:
                i === 0
                  ? "None yet"
                  : i === data.byStepsDone.length - 1
                    ? `All ${i}`
                    : `${i} of ${data.byStepsDone.length - 1}`,
              count,
            }))}
          />
          <ChipGroup title="Registered on each platform" chips={data.byPlatform} />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <SearchBox
          value={filter.search}
          onChange={(search) => onFilter({ search })}
          placeholder="Search name, email or ID"
        />
        <FilterSelect
          label="Track"
          value={filter.track}
          options={TRACKS.map(([key, label]): [Track, string] => {
            const count = data?.byTrack.find((t) => t.key === key)?.count;
            return [key, count != null ? `${label} (${count.toLocaleString("en-US")})` : label];
          })}
          onChange={(track) => onFilter({ track })}
        />
        <FilterSelect
          label="Stage"
          value={filter.stage}
          options={STAGES}
          onChange={(stage) => onFilter({ stage })}
        />
        <button
          type="button"
          aria-pressed={filter.stalledOnly}
          onClick={() => onFilter({ stalledOnly: !filter.stalledOnly })}
          className={`h-10 rounded-[10px] border-[1.5px] px-3 text-[13.5px] font-semibold ${
            filter.stalledOnly
              ? "border-[#F99F1C] bg-[#fff1d9] text-[#8a5300]"
              : "border-border bg-surface text-text"
          }`}
        >
          {filter.stalledOnly ? "✓ " : ""}Stalled only
        </button>
        {filtered ? (
          <button
            type="button"
            onClick={() => onFilter({ search: "", track: "", stage: "", stalledOnly: false })}
            className="h-10 px-2 text-[13px] font-semibold text-accent hover:underline"
          >
            Clear filters
          </button>
        ) : null}
      </div>

      <DataTable
        rows={data?.rows ?? []}
        columns={columns}
        rowKey={(r) => r.userId}
        onRowClick={onOpen}
        selectedKey={selectedId}
        loading={loading}
        rowLabel={(r) => `Open ${`${r.firstName} ${r.lastName}`.trim() || r.email}`}
        empty={
          filtered ? "No participants match these filters." : "No participants have signed in yet."
        }
      />
      <Pager page={page} total={data?.total ?? 0} size={ADMIN_PAGE_SIZE} onPage={onPage} />
    </div>
  );
}

function ChipGroup({
  title,
  chips,
}: {
  title: string;
  chips: { key: string; label: string; count: number }[];
}) {
  return (
    <section className="rounded-[14px] border border-border bg-surface p-4">
      <h3
        className="m-0 text-[11.5px] font-bold uppercase tracking-[0.07em] text-text-muted"
        style={{ fontFamily: "var(--font-body)" }}
      >
        {title}
      </h3>
      <ul className="m-0 mt-2 flex list-none flex-wrap gap-1.5 p-0">
        {chips.length ? (
          chips.map((c) => (
            <li
              key={c.key}
              className="rounded-full border border-border bg-surface-high px-3 py-1 text-[12.5px]"
            >
              {c.label} · <b className="tabular">{c.count.toLocaleString("en-US")}</b>
            </li>
          ))
        ) : (
          <li className="text-[13px] text-text-muted">None yet.</li>
        )}
      </ul>
    </section>
  );
}
